import React, { useEffect, useId, useRef, useState } from 'react';
import { useProductByHandle } from '@backpackjs/storefront';
import { Image, Svg } from '../../snippets';

export default function VideoSlide({
  videoSlide,
  activeVideoSlide,
  setActiveVideoSlide,
}) {
  const { videoUrl, videoPoster, product } = videoSlide;

  const videoRef = useRef(null);
  const videoSlideId = useId();

  const [isHovered, setIsHovered] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  const { product: fullProduct } = useProductByHandle({
    handle: product?.data?.handle,
  });

  const priceMin = parseFloat(fullProduct?.priceRange?.min || 0);
  const priceMax = parseFloat(fullProduct?.priceRange?.max || 0);

  /* --- EVENT HANDLERS --- */
  const handleClick = () => {
    if (isMuted && isPlaying) {
      setIsMuted(false);
    } else if (!isMuted && isPlaying) {
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
    }
  };

  /* --- EFFECTS --- */
  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        setIsPlaying(true);
      }
    }
  }, [isHovered]);

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play();
        setActiveVideoSlide(videoSlideId);
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (activeVideoSlide !== videoSlideId) {
      setIsPlaying(false);
    }
  }, [activeVideoSlide, videoSlide]);

  return (
    <div>
      <div
        className="relative aspect-[3/4] w-full"
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          ref={videoRef}
          src={videoUrl}
          poster={videoPoster?.src}
          loop
          playsInline
          muted={isMuted}
          className="h-full w-full object-cover"
          onClick={handleClick}
        />

        <div className="absolute right-1 top-1 text-white">
          <button
            type="button"
            className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-primary p-1"
            onClick={() => setIsMuted(!isMuted)}
            aria-label={isMuted ? 'Un-mute video' : 'Mute video'}
          >
            {isMuted ? (
              <Svg
                src="/svgs/video-sound-off.svg#video-sound-off"
                viewBox="0 0 24 24"
              />
            ) : (
              <Svg
                src="/svgs/video-sound-on.svg#video-sound-on"
                viewBox="0 0 24 24"
              />
            )}
          </button>
        </div>

        <div className="absolute bottom-1 right-1 text-white">
          <button
            type="button"
            className={`${isHovered ? 'opacity-50' : 'opacity-0'} ${
              isPlaying ? 'opacity-50' : '!opacity-100'
            } flex h-[28px] w-[28px] items-center justify-center rounded-full bg-primary p-1 transition-opacity duration-200`}
            onClick={() => setIsPlaying(!isPlaying)}
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              <Svg
                src="/svgs/video-pause.svg#video-pause"
                viewBox="0 0 36 36"
              />
            ) : (
              <Svg src="/svgs/video-play.svg#video-play" viewBox="0 0 36 36" />
            )}
          </button>
        </div>
      </div>
      {product && (
        <div className="relative mt-6 border-t border-[#DDE2E7] pt-6 text-xs">
          <div className="overflow flex flex-row text-primary">
            <div className="w-[96px] shrink-0">
              {product?.data?.images?.[0]?.originalSrc && (
                <div className="relative aspect-square w-full border border-[#DDE2E7] bg-white">
                  <Image
                    src={product?.data?.images?.[0]?.originalSrc}
                    alt={
                      product?.data?.images?.[0]?.altText ??
                      product?.data?.title
                    }
                    loading="lazy"
                    quality={100}
                    sizes="400px"
                    fill
                  />
                </div>
              )}
            </div>
            <div className="w-[calc(100%-96px)] pl-4">
              {product?.data?.title && product?.data?.handle && (
                <a
                  className="after:absolute after:inset-0 after:block after:h-full after:w-full after:content-['']"
                  href={`/products/${product?.data?.handle}`}
                >
                  <h3 className="overflow-hidden text-ellipsis whitespace-nowrap text-lg font-light leading-normal">
                    {product.data.title}
                  </h3>
                </a>
              )}

              <div className="mt-2 text-[16px]">
                {priceMin === priceMax
                  ? `$${priceMin.toFixed(2)}`
                  : `$${priceMin.toFixed(2)} - $${priceMax.toFixed(2)}`}
              </div>

              <div className="mt-3 flex aspect-square w-[32px] items-center justify-center rounded-full border bg-secondary text-white">
                <Svg
                  className="w-5 text-current"
                  src="/svgs/plus.svg#plus"
                  title="Down"
                  viewBox="0 0 24 24"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
